import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import yaml from 'js-yaml';
import {
  asBool,
  asNumber,
  asString,
  cloneDoc,
  ensureArray,
  ensureObject,
  objectMap,
  objectRecord,
} from '../../../lib/tenant-config-form-utils';
import {
  CheckboxField,
  ColorPickerField,
  FieldLabel,
  ItemCard,
  NumberInput,
  RemoveButton,
  SectionBox,
  StringListEditor,
  SubsectionTitle,
  TextInput,
} from '../tenant-config-form/FormPrimitives';

type ConfigDoc = Record<string, unknown>;

interface V2ConfigSliceEditorProps {
  doc: ConfigDoc;
  onChange: (doc: ConfigDoc) => void;
  scope: 'master' | 'tenant';
}

function yamlBlock(value: Record<string, unknown>): string {
  if (Object.keys(value).length === 0) return '';
  return yaml.dump(value, { lineWidth: 120, noRefs: true, skipInvalid: true }).trimEnd();
}

function parseObjectBlock(value: string): Record<string, unknown> {
  if (!value.trim()) return {};
  const parsed = yaml.load(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Expected a YAML mapping');
  }
  return parsed as Record<string, unknown>;
}

function YamlObjectField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const source = yamlBlock(value);
  const [draft, setDraft] = useState(source);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(source);
    setError(null);
  }, [source]);

  return (
    <FieldLabel label={label}>
      <textarea
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          setError(null);
        }}
        onBlur={() => {
          try {
            const parsed = parseObjectBlock(draft);
            setError(null);
            onChange(parsed);
          } catch (err: any) {
            setError(err?.message || 'Invalid YAML');
          }
        }}
        spellCheck={false}
        className="min-h-28 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-mono text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      {error ? <p className="text-[11px] text-amber-600 dark:text-amber-400">{error}</p> : null}
    </FieldLabel>
  );
}

function updateDoc(onChange: (doc: ConfigDoc) => void, doc: ConfigDoc, mutator: (next: ConfigDoc) => void) {
  const next = cloneDoc(doc);
  mutator(next);
  onChange(next);
}

function ensureNestedObject(root: Record<string, unknown>, path: string[]): Record<string, unknown> {
  let current = root;
  for (const segment of path) {
    current = ensureObject(current, segment);
  }
  return current;
}

function getV2Cloud(doc: ConfigDoc): Record<string, unknown> {
  return objectRecord(doc.cloud);
}

function getV2Devops(doc: ConfigDoc): Record<string, unknown> {
  return objectRecord(doc.devops);
}

function getProviderLabel(providerId: string, provider: Record<string, unknown>): string {
  return asString(provider.location) || asString(provider.region) || providerId;
}

export function V2ConfigSliceEditor({ doc, onChange, scope }: V2ConfigSliceEditorProps) {
  if (doc.schemaVersion !== 2) {
    return null;
  }

  const isMaster = scope === 'master';
  const isTenant = scope === 'tenant';

  // Platform (master scope)
  const platform = objectRecord(doc.platform);
  const platformPortal = objectRecord(platform.portal);
  const platformPortalClusterStatus = objectRecord(platformPortal.clusterStatus);
  const platformIntegrations = objectRecord(platform.integrations);
  const platformCicd = objectRecord(platformIntegrations.cicd);
  const platformGithub = objectRecord(platformIntegrations.github);
  const platformAzure = objectRecord(platformIntegrations.azure);
  const platformAzureSp = objectRecord(platformAzure.servicePrincipal);
  const platformAzurePublisher = objectRecord(platformAzure.teamsPublisher);
  const platformAzureDevOps = objectRecord(platformIntegrations.azureDevOps);
  const platformOpenai = objectRecord(platformIntegrations.openai);
  const platformClockify = objectRecord(platformIntegrations.clockify);
  const platformAiWorkflows = objectRecord(platformIntegrations.aiWorkflows);
  const platformApprovals = objectRecord(platformIntegrations.approvals);
  const platformApprovalsMicrosoft = objectRecord(platformApprovals.microsoft);
  const platformApprovalsDefaults = objectRecord(platformApprovals.defaults);
  const platformNotifications = objectRecord(platformIntegrations.notifications);
  const platformTeamsInbox = objectRecord(platformNotifications.teams_inbox);

  // Cloud defaults (master scope)
  const cloud = getV2Cloud(doc);
  const cloudDefaults = objectRecord(cloud.defaults);
  const cloudDefaultsMaximo = objectRecord(cloudDefaults.maximo);
  const cloudDefaultsContainers = objectRecord(cloudDefaults.containers);
  const cloudDefaultsDatabase = objectRecord(cloudDefaults.database);
  const cloudDefaultsCluster = objectRecord(cloudDefaults.cluster);
  const cloudDefaultsClusterCfg = objectRecord(cloudDefaultsCluster.configuration);
  const cloudDefaultsClusterCp = objectRecord(cloudDefaultsClusterCfg.controlPlane);
  const cloudDefaultsClusterCompute = objectRecord(cloudDefaultsClusterCfg.compute);
  const cloudDefaultsDeprovision = objectRecord(cloudDefaults.deprovision);

  // Support SLA vault keys (master scope)
  const support = objectRecord(doc.support);
  const supportSlaReport = objectRecord(support.slaReport);
  const slaVaultKeys = objectRecord(supportSlaReport.vaultKeys);

  // Identity (tenant scope)
  const identity = objectRecord(doc.identity);

  // Cloud providers + environments
  const providers = objectMap(cloud.providers);
  const cloudEnvironments = objectMap(cloud.environments);

  // DevOps
  const devops = getV2Devops(doc);
  const products = objectMap(devops.products);
  const lifecycle = objectRecord(devops.lifecycle);
  const lifecycleDefaults = objectRecord(lifecycle.defaults);
  const lifecycleManageDefaults = objectRecord(lifecycleDefaults.manage);
  const lifecycleEnvironments = objectMap(lifecycle.environments);

  const providerNames = Object.keys(providers).sort();
  const environmentNames = Array.from(
    new Set([...Object.keys(cloudEnvironments), ...Object.keys(lifecycleEnvironments)]),
  ).sort();
  const lifecycleEnvironmentNames = Object.keys(lifecycleEnvironments).sort();
  const productNames = Object.keys(products).sort();

  return (
    <SectionBox title="YAML v2 — Configuration">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200">
        This editor writes the v2 structure directly. The YAML tab remains the source of truth for fields not shown here.
      </div>

      {/* ── Tenant: Identity ─────────────────────────────────────────────── */}
      {isTenant ? (
        <>
          <SubsectionTitle>Identity</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="Code">
              <TextInput
                value={asString(identity.code)}
                onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'identity').code = v; })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Display name">
              <TextInput
                value={asString(identity.displayName)}
                onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'identity').displayName = v; })}
              />
            </FieldLabel>
            <FieldLabel label="Cluster code">
              <TextInput
                value={asString(identity.clusterCode)}
                onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'identity').clusterCode = v; })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Brand color">
              <ColorPickerField
                value={asString(identity.color)}
                onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'identity').color = v || undefined; })}
                placeholder="#FFD300"
              />
            </FieldLabel>
            <div className="flex items-end pb-1">
              <CheckboxField
                label="Production tenant"
                checked={asBool(identity.isProduction)}
                onChange={(checked) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'identity').isProduction = checked; })}
              />
            </div>
          </div>
          <FieldLabel label="Domains" className="mt-2">
            <StringListEditor
              items={Array.isArray(identity.domains) ? (identity.domains as string[]) : []}
              onChange={(items) => updateDoc(onChange, doc, (n) => {
                ensureObject(n, 'identity').domains = items.length > 0 ? items : undefined;
              })}
              placeholder="example.com"
            />
          </FieldLabel>
        </>
      ) : null}

      {/* ── Master: Portal ───────────────────────────────────────────────── */}
      {isMaster ? (
        <>
          <SubsectionTitle>Portal</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="Base URL" className="md:col-span-2">
              <TextInput
                value={asString(platformPortal.baseUrl)}
                onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['platform', 'portal']).baseUrl = v; })}
              />
            </FieldLabel>
            <FieldLabel label="Inventory internal token ref">
              <TextInput
                value={asString(platformPortalClusterStatus.inventoryInternalTokenRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'portal', 'clusterStatus']).inventoryInternalTokenRef = v;
                })}
                mono
              />
            </FieldLabel>
          </div>

          {/* ── Master: Integrations ───────────────────────────────────────── */}
          <SubsectionTitle>Integrations</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="CI/CD webhook secret ref">
              <TextInput
                value={asString(platformCicd.webhookSecretRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'cicd']).webhookSecretRef = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="OpenAI key ref">
              <TextInput
                value={asString(platformOpenai.keyRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'openai']).keyRef = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Clockify API key ref">
              <TextInput
                value={asString(platformClockify.apiKeyRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'clockify']).apiKeyRef = v;
                })}
                mono
              />
            </FieldLabel>
          </div>

          {/* ── Master: GitHub ─────────────────────────────────────────────── */}
          <SubsectionTitle>GitHub & Workflows</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="Workflow repo URL" className="md:col-span-2">
              <TextInput
                value={asString(platformGithub.workflowRepoUrl)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'github']).workflowRepoUrl = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Workflow Git PAT ref">
              <TextInput
                value={asString(platformGithub.workflowGitRepoPatRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'github']).workflowGitRepoPatRef = v;
                })}
                mono
              />
            </FieldLabel>
          </div>

          {/* ── Master: Azure ─────────────────────────────────────────────── */}
          <SubsectionTitle>Azure</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="Tenant ID ref">
              <TextInput
                value={asString(platformAzure.tenantIdRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azure']).tenantIdRef = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Service principal client ID ref">
              <TextInput
                value={asString(platformAzureSp.clientIdRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azure', 'servicePrincipal']).clientIdRef = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Service principal secret ref">
              <TextInput
                value={asString(platformAzureSp.clientSecretRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azure', 'servicePrincipal']).clientSecretRef = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Teams publisher username">
              <TextInput
                value={asString(platformAzurePublisher.username)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azure', 'teamsPublisher']).username = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="Teams publisher password ref">
              <TextInput
                value={asString(platformAzurePublisher.passwordRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azure', 'teamsPublisher']).passwordRef = v;
                })}
                mono
              />
            </FieldLabel>
          </div>

          <SubsectionTitle>Azure DevOps</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="Organization">
              <TextInput
                value={asString(platformAzureDevOps.org)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azureDevOps']).org = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="Git username">
              <TextInput
                value={asString(platformAzureDevOps.gitUsername)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azureDevOps']).gitUsername = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="Maximo dev tools repo URL" className="md:col-span-2">
              <TextInput
                value={asString(platformAzureDevOps.maximoDevToolsRepoUrl)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azureDevOps']).maximoDevToolsRepoUrl = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Git password ref">
              <TextInput
                value={asString(platformAzureDevOps.gitPasswordRef)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'azureDevOps']).gitPasswordRef = v;
                })}
                mono
              />
            </FieldLabel>
          </div>

          {/* ── Master: Approvals & Notifications ─────────────────────────── */}
          <SubsectionTitle>Approvals</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CheckboxField
              label="Approvals enabled"
              checked={asBool(platformApprovals.enabled, true)}
              onChange={(c) => updateDoc(onChange, doc, (n) => {
                ensureNestedObject(n, ['platform', 'integrations', 'approvals']).enabled = c;
              })}
            />
            <FieldLabel label="Expiration (hours)">
              <NumberInput
                value={asNumber(platformApprovalsDefaults.expiration_hours, 24)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'approvals', 'defaults']).expiration_hours = v;
                })}
              />
            </FieldLabel>
            <CheckboxField
              label="Notify all by default"
              checked={asBool(platformApprovalsDefaults.notify_all_by_default, true)}
              onChange={(c) => updateDoc(onChange, doc, (n) => {
                ensureNestedObject(n, ['platform', 'integrations', 'approvals', 'defaults']).notify_all_by_default = c;
              })}
            />
          </div>
          <SubsectionTitle>Approvals — Microsoft</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CheckboxField
              label="Microsoft approvals enabled"
              checked={asBool(platformApprovalsMicrosoft.enabled, true)}
              onChange={(c) => updateDoc(onChange, doc, (n) => {
                ensureNestedObject(n, ['platform', 'integrations', 'approvals', 'microsoft']).enabled = c;
              })}
            />
            <FieldLabel label="Client ID">
              <TextInput
                value={asString(platformApprovalsMicrosoft.client_id)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'approvals', 'microsoft']).client_id = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Client secret">
              <TextInput
                value={asString(platformApprovalsMicrosoft.client_secret)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'approvals', 'microsoft']).client_secret = v;
                })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="Tenant ID">
              <TextInput
                value={asString(platformApprovalsMicrosoft.tenant_id)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'approvals', 'microsoft']).tenant_id = v;
                })}
                mono
              />
            </FieldLabel>
          </div>
          <SubsectionTitle>Notifications</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="Teams inbox portal user email">
              <TextInput
                value={asString(platformTeamsInbox.portal_user_email)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'notifications', 'teams_inbox']).portal_user_email = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="Teams inbox max messages">
              <NumberInput
                value={asNumber(platformTeamsInbox.max_messages, 200)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['platform', 'integrations', 'notifications', 'teams_inbox']).max_messages = v;
                })}
              />
            </FieldLabel>
          </div>

          {/* ── Master: AI Workflows ───────────────────────────────────────── */}
          <SubsectionTitle>AI Workflows</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(
              [
                'mcp_api_key_ref', 'ado_pat_ref', 'github_pat_ref', 'github_org',
                'hubspot_private_app_token_ref', 'graph_client_id_ref', 'graph_client_secret_ref',
                'graph_tenant_id_ref', 'azure_subscription_id', 'bot_resource_group', 'workflow_cursor_provider',
              ] as const
            ).map((field) => (
              <FieldLabel key={field} label={field.replace(/_/g, ' ')}>
                <TextInput
                  value={asString(platformAiWorkflows[field])}
                  onChange={(v) => updateDoc(onChange, doc, (n) => {
                    ensureNestedObject(n, ['platform', 'integrations', 'aiWorkflows'])[field] = v || undefined;
                  })}
                  mono
                />
              </FieldLabel>
            ))}
          </div>

          {/* ── Master: Cloud Defaults ─────────────────────────────────────── */}
          <SubsectionTitle>Cloud Defaults — Maximo</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldLabel label="Port">
              <NumberInput
                value={asNumber(cloudDefaultsMaximo.port, 9080)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['cloud', 'defaults', 'maximo']).port = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="HTTPS port">
              <NumberInput
                value={asNumber(
                  cloudDefaultsMaximo.https_port !== undefined ? cloudDefaultsMaximo.https_port : cloudDefaultsMaximo.httpsPort,
                  9443,
                )}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  const m = ensureNestedObject(n, ['cloud', 'defaults', 'maximo']);
                  if ('httpsPort' in m) { m.httpsPort = v; } else { m.https_port = v; }
                })}
              />
            </FieldLabel>
            <FieldLabel label="User">
              <TextInput
                value={asString(cloudDefaultsMaximo.user)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['cloud', 'defaults', 'maximo']).user = v;
                })}
              />
            </FieldLabel>
          </div>

          <SubsectionTitle>Cloud Defaults — Cluster</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldLabel label="OpenShift version">
              <TextInput
                value={asString(cloudDefaultsClusterCfg.openshiftVersion)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['cloud', 'defaults', 'cluster', 'configuration']).openshiftVersion = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="Control plane count">
              <NumberInput
                value={asNumber(cloudDefaultsClusterCp.count, 1)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['cloud', 'defaults', 'cluster', 'configuration', 'controlPlane']).count = v;
                })}
              />
            </FieldLabel>
            <FieldLabel label="Compute count">
              <NumberInput
                value={asNumber(cloudDefaultsClusterCompute.count, 0)}
                onChange={(v) => updateDoc(onChange, doc, (n) => {
                  ensureNestedObject(n, ['cloud', 'defaults', 'cluster', 'configuration', 'compute']).count = v;
                })}
              />
            </FieldLabel>
          </div>
          <div className="mt-2">
            <CheckboxField
              label="Deprovision delete primary IP"
              checked={asBool(cloudDefaultsDeprovision.deletePrimaryIp)}
              onChange={(checked) => updateDoc(onChange, doc, (n) => {
                ensureNestedObject(n, ['cloud', 'defaults', 'deprovision']).deletePrimaryIp = checked;
              })}
            />
          </div>

          <SubsectionTitle>Cloud Defaults — Database</SubsectionTitle>
          <FieldLabel label="Port">
            <NumberInput
              value={asNumber(cloudDefaultsDatabase.port, 50000)}
              onChange={(v) => updateDoc(onChange, doc, (n) => {
                ensureNestedObject(n, ['cloud', 'defaults', 'database']).port = v;
              })}
            />
          </FieldLabel>

          <SubsectionTitle>Cloud Defaults — Container templates</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldLabel label="App container">
              <TextInput value={asString(cloudDefaultsContainers.app_container_name)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'defaults', 'containers']).app_container_name = v; })} mono />
            </FieldLabel>
            <FieldLabel label="DB container">
              <TextInput value={asString(cloudDefaultsContainers.db_container_name)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'defaults', 'containers']).db_container_name = v; })} mono />
            </FieldLabel>
            <FieldLabel label="Admin container">
              <TextInput value={asString(cloudDefaultsContainers.admin_container_name)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'defaults', 'containers']).admin_container_name = v; })} mono />
            </FieldLabel>
            <FieldLabel label="Network name">
              <TextInput value={asString(cloudDefaultsContainers.network_name)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'defaults', 'containers']).network_name = v; })} mono />
            </FieldLabel>
          </div>

          {/* ── Master: SLA report vault keys ─────────────────────────────── */}
          <SubsectionTitle>SLA report vault keys</SubsectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(['jiraToken', 'openaiApiKey', 'clockifyApiKey', 'azureDevOpsPat'] as const).map((key) => (
              <FieldLabel key={key} label={key}>
                <TextInput
                  value={asString(slaVaultKeys[key])}
                  onChange={(v) => updateDoc(onChange, doc, (n) => {
                    ensureNestedObject(n, ['support', 'slaReport', 'vaultKeys'])[key] = v;
                  })}
                  mono
                />
              </FieldLabel>
            ))}
          </div>
        </>
      ) : null}

      {/* ── Cloud Providers (expanded, both scopes) ────────────────────────── */}
      <SubsectionTitle>Cloud Providers</SubsectionTitle>
      <div className="space-y-2">
        {providerNames.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">No v2 providers configured.</p>
        ) : null}
        {providerNames.map((providerId) => {
          const provider = providers[providerId];
          const isAws = providerId === 'aws';
          const isOci = providerId === 'oci';
          const isHetzner = providerId === 'hetzner';
          const isIbm = providerId === 'ibm';

          const awsEcr = isAws ? objectRecord(provider.ecr) : {};
          const awsRoute53 = isAws ? objectRecord(provider.route53) : {};
          const awsS3 = isAws ? objectRecord(provider.s3) : {};
          const awsSes = isAws ? objectRecord(provider.ses) : {};
          const awsRoles = isAws ? objectRecord(provider.roles) : {};
          const awsRoAdmin = isAws ? objectRecord(awsRoles.readOnlyAdmin) : {};
          const awsPower = isAws ? objectRecord(awsRoles.power) : {};
          const ociCli = isOci ? objectRecord(provider.cli) : {};
          const ociS3 = isOci ? objectRecord(provider.s3) : {};
          const hetznerS3 = isHetzner ? objectRecord(provider.s3) : {};

          return (
            <ItemCard key={providerId}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-sm font-semibold">{providerId}</div>
                  <div className="text-xs text-gray-500">{getProviderLabel(providerId, provider)}</div>
                </div>
                <RemoveButton
                  label={`Remove provider ${providerId}`}
                  onClick={() => updateDoc(onChange, doc, (n) => {
                    const p = ensureNestedObject(n, ['cloud', 'providers']);
                    delete p[providerId];
                  })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <FieldLabel label="Region">
                  <TextInput
                    value={asString(provider.region)}
                    onChange={(v) => updateDoc(onChange, doc, (n) => {
                      ensureNestedObject(n, ['cloud', 'providers', providerId]).region = v || undefined;
                    })}
                    mono
                  />
                </FieldLabel>
                <FieldLabel label="Location">
                  <TextInput
                    value={asString(provider.location)}
                    onChange={(v) => updateDoc(onChange, doc, (n) => {
                      ensureNestedObject(n, ['cloud', 'providers', providerId]).location = v || undefined;
                    })}
                    mono
                  />
                </FieldLabel>
                {isTenant ? (
                  <FieldLabel label="Tenant name">
                    <TextInput
                      value={asString(provider.tenantName)}
                      onChange={(v) => updateDoc(onChange, doc, (n) => {
                        ensureNestedObject(n, ['cloud', 'providers', providerId]).tenantName = v || undefined;
                      })}
                    />
                  </FieldLabel>
                ) : null}
              </div>

              {isTenant && (isAws || isOci) ? (
                <FieldLabel label="Enabled regions" className="mt-2">
                  <StringListEditor
                    items={Array.isArray(provider.enabledRegions) ? (provider.enabledRegions as string[]) : []}
                    onChange={(items) => updateDoc(onChange, doc, (n) => {
                      ensureNestedObject(n, ['cloud', 'providers', providerId]).enabledRegions = items.length > 0 ? items : undefined;
                    })}
                    placeholder="eu-west-1"
                  />
                </FieldLabel>
              ) : null}

              {/* AWS */}
              {isAws ? (
                <>
                  <SubsectionTitle>ECR</SubsectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FieldLabel label="Registry" className="md:col-span-2">
                      <TextInput value={asString(awsEcr.registry)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ecr']).registry = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Access key ref">
                      <TextInput value={asString(awsEcr.accessKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ecr']).accessKeyRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Secret key ref">
                      <TextInput value={asString(awsEcr.secretKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ecr']).secretKeyRef = v; })} mono />
                    </FieldLabel>
                  </div>
                  <SubsectionTitle>Route53</SubsectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FieldLabel label="Hosted zone ID">
                      <TextInput value={asString(awsRoute53.hostedZoneId)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'route53']).hostedZoneId = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Access key ref">
                      <TextInput value={asString(awsRoute53.accessKeyIdRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'route53']).accessKeyIdRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Secret key ref">
                      <TextInput value={asString(awsRoute53.secretAccessKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'route53']).secretAccessKeyRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Region">
                      <TextInput value={asString(awsRoute53.region)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'route53']).region = v || undefined; })} mono />
                    </FieldLabel>
                    <FieldLabel label="TTL">
                      <NumberInput value={asNumber(awsRoute53.ttl, 60)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'route53']).ttl = v; })} />
                    </FieldLabel>
                  </div>
                  <SubsectionTitle>S3</SubsectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldLabel label="Config bucket">
                      <TextInput value={asString(awsS3.configBucket)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).configBucket = v; })} />
                    </FieldLabel>
                    <FieldLabel label="Masconfig bucket">
                      <TextInput value={asString(awsS3.masconfigBucket)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).masconfigBucket = v; })} />
                    </FieldLabel>
                    <FieldLabel label="Shared region">
                      <TextInput value={asString(awsS3.sharedRegion)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).sharedRegion = v || undefined; })} mono />
                    </FieldLabel>
                    <FieldLabel label="SLS license object">
                      <TextInput value={asString(awsS3.slsLicenseObject)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).slsLicenseObject = v || undefined; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Pull secret filename">
                      <TextInput value={asString(awsS3.pullSecretFileName)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).pullSecretFileName = v || undefined; })} />
                    </FieldLabel>
                    <FieldLabel label="Access key ref">
                      <TextInput value={asString(awsS3.accessKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).accessKeyRef = v || undefined; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Secret key ref">
                      <TextInput value={asString(awsS3.secretKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).secretKeyRef = v || undefined; })} mono />
                    </FieldLabel>
                  </div>
                  <SubsectionTitle>SES</SubsectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldLabel label="Admin email">
                      <TextInput value={asString(awsSes.adminEmail)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ses']).adminEmail = v; })} />
                    </FieldLabel>
                    <FieldLabel label="Mail host">
                      <TextInput value={asString(awsSes.mailHost)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ses']).mailHost = v; })} />
                    </FieldLabel>
                    <FieldLabel label="Mail port">
                      <NumberInput value={asNumber(awsSes.mailPort, 587)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ses']).mailPort = v; })} />
                    </FieldLabel>
                    <FieldLabel label="Mail user">
                      <TextInput value={asString(awsSes.mailUser)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ses']).mailUser = v; })} />
                    </FieldLabel>
                    <FieldLabel label="Mail pass ref">
                      <TextInput value={asString(awsSes.mailPass)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'ses']).mailPass = v; })} mono />
                    </FieldLabel>
                  </div>
                  {isTenant ? (
                    <>
                      <SubsectionTitle>Roles — Read-only admin</SubsectionTitle>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <FieldLabel label="Access key ID ref">
                          <TextInput value={asString(awsRoAdmin.access_key_id_ref)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'roles', 'readOnlyAdmin']).access_key_id_ref = v; })} mono />
                        </FieldLabel>
                        <FieldLabel label="Secret access key ref">
                          <TextInput value={asString(awsRoAdmin.secret_access_key_ref)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'roles', 'readOnlyAdmin']).secret_access_key_ref = v; })} mono />
                        </FieldLabel>
                        <FieldLabel label="Region">
                          <TextInput value={asString(awsRoAdmin.region)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'roles', 'readOnlyAdmin']).region = v || undefined; })} mono />
                        </FieldLabel>
                      </div>
                      <SubsectionTitle>Roles — Power</SubsectionTitle>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FieldLabel label="Access key ID ref">
                          <TextInput value={asString(awsPower.access_key_id_ref)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'roles', 'power']).access_key_id_ref = v; })} mono />
                        </FieldLabel>
                        <FieldLabel label="Secret access key ref">
                          <TextInput value={asString(awsPower.secret_access_key_ref)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'roles', 'power']).secret_access_key_ref = v; })} mono />
                        </FieldLabel>
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}

              {/* OCI */}
              {isOci ? (
                <>
                  <SubsectionTitle>CLI credentials</SubsectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldLabel label="Namespace">
                      <TextInput value={asString(provider.namespace)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId]).namespace = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="CLI profile">
                      <TextInput value={asString(ociCli.profile)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).profile = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="CLI user ref">
                      <TextInput value={asString(ociCli.userRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).userRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Fingerprint ref">
                      <TextInput value={asString(ociCli.fingerprintRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).fingerprintRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Tenancy ID ref">
                      <TextInput value={asString(ociCli.tenancyIdRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).tenancyIdRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Key content ref">
                      <TextInput value={asString(ociCli.keyContentRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).keyContentRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Vault ID ref">
                      <TextInput value={asString(ociCli.vaultIdRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).vaultIdRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Vault compartment ID ref">
                      <TextInput value={asString(ociCli.vaultCompartmentIdRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 'cli']).vaultCompartmentIdRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="S3 masconfig bucket">
                      <TextInput value={asString(ociS3.masconfigBucket)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).masconfigBucket = v; })} />
                    </FieldLabel>
                    <FieldLabel label="S3 access key ref">
                      <TextInput value={asString(ociS3.accessKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).accessKeyRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="S3 secret key ref">
                      <TextInput value={asString(ociS3.secretKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).secretKeyRef = v; })} mono />
                    </FieldLabel>
                  </div>
                </>
              ) : null}

              {/* Hetzner */}
              {isHetzner ? (
                <>
                  <SubsectionTitle>SSH & S3</SubsectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldLabel label="Token ref">
                      <TextInput value={asString(provider.tokenRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId]).tokenRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="Cloud SSH key name">
                      <TextInput value={asString(provider.cloudSshKeyName)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId]).cloudSshKeyName = v; })} />
                    </FieldLabel>
                    <FieldLabel label="SSH public key name">
                      <TextInput value={asString(provider.sshPublicKeyName)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId]).sshPublicKeyName = v; })} />
                    </FieldLabel>
                    <FieldLabel label="SSH private key ref">
                      <TextInput value={asString(provider.sshPrivateKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId]).sshPrivateKeyRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="S3 masconfig bucket">
                      <TextInput value={asString(hetznerS3.masconfigBucket)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).masconfigBucket = v; })} />
                    </FieldLabel>
                    <FieldLabel label="S3 access key ref">
                      <TextInput value={asString(hetznerS3.accessKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).accessKeyRef = v; })} mono />
                    </FieldLabel>
                    <FieldLabel label="S3 secret key ref">
                      <TextInput value={asString(hetznerS3.secretKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId, 's3']).secretKeyRef = v; })} mono />
                    </FieldLabel>
                  </div>
                </>
              ) : null}

              {/* IBM */}
              {isIbm ? (
                <div className="mt-3">
                  <FieldLabel label="Entitlement key ref">
                    <TextInput value={asString(provider.entitlementKeyRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'providers', providerId]).entitlementKeyRef = v; })} mono />
                  </FieldLabel>
                </div>
              ) : null}
            </ItemCard>
          );
        })}
        <button
          type="button"
          onClick={() => updateDoc(onChange, doc, (n) => {
            const p = ensureNestedObject(n, ['cloud', 'providers']);
            const id = `provider-${Object.keys(p).length + 1}`;
            p[id] = { region: '', location: '' };
          })}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          <Plus className="h-3.5 w-3.5" />
          Add provider
        </button>
      </div>

      {/* ── Cloud Environments ─────────────────────────────────────────────── */}
      <SubsectionTitle>Cloud Environments</SubsectionTitle>
      <div className="space-y-2">
        {environmentNames.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">No v2 environments configured.</p>
        ) : null}
        {environmentNames.map((envName) => {
          const env = cloudEnvironments[envName] || {};
          const lifecycleEnv = lifecycleEnvironments[envName] || {};
          const deploy = objectRecord(lifecycleEnv.deploy);
          const productRefs = Array.isArray(deploy.productRefs) ? (deploy.productRefs as Record<string, unknown>[]) : [];
          const envCredentials = objectRecord(env.credentials);
          return (
            <ItemCard key={envName}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-sm font-semibold">{envName}</div>
                  <div className="text-xs text-gray-500">
                    {asString(env.kind) || 'env'} / {asString(env.runtime) || 'runtime'} / {asString(env.provider) || 'provider'}
                  </div>
                </div>
                <RemoveButton
                  label={`Remove environment ${envName}`}
                  onClick={() => updateDoc(onChange, doc, (n) => {
                    delete ensureNestedObject(n, ['cloud', 'environments'])[envName];
                    delete ensureNestedObject(n, ['devops', 'lifecycle', 'environments'])[envName];
                  })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <FieldLabel label="Kind">
                  <TextInput value={asString(env.kind)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName]).kind = v; })} mono />
                </FieldLabel>
                <FieldLabel label="Provider">
                  <TextInput value={asString(env.provider)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName]).provider = v; })} mono />
                </FieldLabel>
                <FieldLabel label="Runtime">
                  <TextInput value={asString(env.runtime)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName]).runtime = v; })} mono />
                </FieldLabel>
                <FieldLabel label="Stack">
                  <TextInput value={asString(lifecycleEnv.stack)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName]).stack = v; })} mono />
                </FieldLabel>
                <FieldLabel label="Cluster code">
                  <TextInput value={asString(env.clusterCode)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName]).clusterCode = v || undefined; })} mono />
                </FieldLabel>
                <FieldLabel label="Instance code">
                  <TextInput value={asString(env.instanceCode)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName]).instanceCode = v || undefined; })} mono />
                </FieldLabel>
                <FieldLabel label="Base domain">
                  <TextInput value={asString(objectRecord(env.dns).baseDomain)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName, 'dns']).baseDomain = v || undefined; })} />
                </FieldLabel>
                <FieldLabel label="Manage network">
                  <TextInput value={asString(objectRecord(objectRecord(lifecycleEnv.manage).runtime).networkName)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'manage', 'runtime']).networkName = v || undefined; })} mono />
                </FieldLabel>
                <FieldLabel label="Maximo password ref">
                  <TextInput value={asString(envCredentials.maximoPasswordRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName, 'credentials']).maximoPasswordRef = v || undefined; })} mono />
                </FieldLabel>
                <FieldLabel label="Database password hash ref">
                  <TextInput value={asString(envCredentials.databasePasswordHashRef)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['cloud', 'environments', envName, 'credentials']).databasePasswordHashRef = v || undefined; })} mono />
                </FieldLabel>
              </div>
              <SubsectionTitle>Deploy Products</SubsectionTitle>
              <div className="space-y-2">
                {productRefs.map((ref, index) => (
                  <div key={`${envName}-product-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                    <FieldLabel label="Product">
                      <TextInput value={asString(ref.product)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs') as Record<string, unknown>[];
                        refs[index] = { ...(refs[index] || {}), product: v };
                      })} mono />
                    </FieldLabel>
                    <FieldLabel label="Branch">
                      <TextInput value={asString(ref.branch)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs') as Record<string, unknown>[];
                        refs[index] = { ...(refs[index] || {}), branch: v };
                      })} mono />
                    </FieldLabel>
                    <CheckboxField
                      label="PR env"
                      checked={asBool(ref.buildPrEnvOnOpen)}
                      onChange={(checked) => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs') as Record<string, unknown>[];
                        refs[index] = { ...(refs[index] || {}), buildPrEnvOnOpen: checked };
                      })}
                    />
                    <RemoveButton
                      label={`Remove deploy product ${index + 1} from ${envName}`}
                      onClick={() => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs');
                        refs.splice(index, 1);
                      })}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => updateDoc(onChange, doc, (n) => {
                    ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs')
                      .push({ product: productNames[0] || '', branch: 'main', buildPrEnvOnOpen: false });
                  })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add deploy product
                </button>
              </div>
            </ItemCard>
          );
        })}
        <button
          type="button"
          onClick={() => updateDoc(onChange, doc, (n) => {
            const envs = ensureNestedObject(n, ['cloud', 'environments']);
            const name = `env-${Object.keys(envs).length + 1}`;
            envs[name] = { kind: 'dev', provider: 'hetzner', runtime: 'compose' };
            ensureNestedObject(n, ['devops', 'lifecycle', 'environments', name]).stack = 'manage';
          })}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          <Plus className="h-3.5 w-3.5" />
          Add environment
        </button>
      </div>

      {/* ── DevOps Lifecycle Environments ─────────────────────────────────── */}
      <SubsectionTitle>DevOps Lifecycle Environments</SubsectionTitle>
      <div className="space-y-2">
        {lifecycleEnvironmentNames.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">No v2 devops lifecycle environments configured.</p>
        ) : null}
        {lifecycleEnvironmentNames.map((envName) => {
          const lifecycleEnv = lifecycleEnvironments[envName] || {};
          const deploy = objectRecord(lifecycleEnv.deploy);
          const manage = objectRecord(lifecycleEnv.manage);
          const images = objectRecord(lifecycleEnv.images);
          const masRebuild = objectRecord(lifecycleEnv.masRebuild);
          const productRefs = Array.isArray(deploy.productRefs) ? (deploy.productRefs as Record<string, unknown>[]) : [];

          return (
            <ItemCard key={`devops-${envName}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-sm font-semibold">{envName}</div>
                  <div className="text-xs text-gray-500">
                    {asString(lifecycleEnv.stack) || 'stack'} / {productRefs.length} deploy product{productRefs.length === 1 ? '' : 's'}
                  </div>
                </div>
                <RemoveButton
                  label={`Remove devops lifecycle environment ${envName}`}
                  onClick={() => updateDoc(onChange, doc, (n) => {
                    delete ensureNestedObject(n, ['devops', 'lifecycle', 'environments'])[envName];
                  })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <FieldLabel label="Stack">
                  <TextInput
                    value={asString(lifecycleEnv.stack)}
                    onChange={(v) => updateDoc(onChange, doc, (n) => {
                      ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName]).stack = v || undefined;
                    })}
                    mono
                  />
                </FieldLabel>
              </div>

              <SubsectionTitle>Deploy Product Refs</SubsectionTitle>
              <div className="space-y-2">
                {productRefs.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">No deploy product refs configured.</p>
                ) : null}
                {productRefs.map((ref, index) => (
                  <div key={`${envName}-lifecycle-product-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                    <FieldLabel label="Product">
                      <TextInput value={asString(ref.product)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs') as Record<string, unknown>[];
                        refs[index] = { ...(refs[index] || {}), product: v };
                      })} mono />
                    </FieldLabel>
                    <FieldLabel label="Branch">
                      <TextInput value={asString(ref.branch)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs') as Record<string, unknown>[];
                        refs[index] = { ...(refs[index] || {}), branch: v };
                      })} mono />
                    </FieldLabel>
                    <CheckboxField
                      label="PR env"
                      checked={asBool(ref.buildPrEnvOnOpen)}
                      onChange={(checked) => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs') as Record<string, unknown>[];
                        refs[index] = { ...(refs[index] || {}), buildPrEnvOnOpen: checked };
                      })}
                    />
                    <RemoveButton
                      label={`Remove deploy product ${index + 1} from ${envName}`}
                      onClick={() => updateDoc(onChange, doc, (n) => {
                        const refs = ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs');
                        refs.splice(index, 1);
                      })}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => updateDoc(onChange, doc, (n) => {
                    ensureArray(ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName, 'deploy']), 'productRefs')
                      .push({ product: productNames[0] || '', branch: 'main', buildPrEnvOnOpen: false });
                  })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add deploy product ref
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <YamlObjectField
                  label="Manage YAML"
                  value={manage}
                  onChange={(value) => updateDoc(onChange, doc, (n) => {
                    ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName]).manage = value;
                  })}
                />
                <YamlObjectField
                  label="Images YAML"
                  value={images}
                  onChange={(value) => updateDoc(onChange, doc, (n) => {
                    ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName]).images = value;
                  })}
                />
                <YamlObjectField
                  label="MAS rebuild YAML"
                  value={masRebuild}
                  onChange={(value) => updateDoc(onChange, doc, (n) => {
                    ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName]).masRebuild = value;
                  })}
                />
                <YamlObjectField
                  label="Deploy YAML"
                  value={deploy}
                  onChange={(value) => updateDoc(onChange, doc, (n) => {
                    ensureNestedObject(n, ['devops', 'lifecycle', 'environments', envName]).deploy = value;
                  })}
                />
              </div>
            </ItemCard>
          );
        })}
        <button
          type="button"
          onClick={() => updateDoc(onChange, doc, (n) => {
            const envs = ensureNestedObject(n, ['devops', 'lifecycle', 'environments']);
            const name = `env-${Object.keys(envs).length + 1}`;
            envs[name] = {
              stack: 'manage',
              deploy: { productRefs: [] },
              manage: {},
            };
          })}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          <Plus className="h-3.5 w-3.5" />
          Add devops lifecycle environment
        </button>
      </div>

      {/* ── DevOps Products ────────────────────────────────────────────────── */}
      <SubsectionTitle>DevOps Products</SubsectionTitle>
      <div className="space-y-2">
        {productNames.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">No v2 products configured.</p>
        ) : null}
        {productNames.map((productId) => {
          const product = products[productId];
          const repos = Array.isArray(product.repos) ? (product.repos as Record<string, unknown>[]) : [];
          const webhook = objectRecord(product.webhook);
          return (
            <ItemCard key={productId}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono text-sm font-semibold">{productId}</div>
                <RemoveButton label={`Remove product ${productId}`} onClick={() => updateDoc(onChange, doc, (n) => { delete ensureNestedObject(n, ['devops', 'products'])[productId]; })} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <FieldLabel label="Workspace dir">
                  <TextInput value={asString(product.workspaceDir)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['devops', 'products', productId]).workspaceDir = v; })} mono />
                </FieldLabel>
                <FieldLabel label="PR TTL minutes">
                  <NumberInput value={asNumber(product.prEnvTTLMinutes, 120)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['devops', 'products', productId]).prEnvTTLMinutes = v; })} />
                </FieldLabel>
                <div className="flex items-end gap-3 pb-1">
                  <CheckboxField
                    label="Webhook enabled"
                    checked={asBool(webhook.enabled)}
                    onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['devops', 'products', productId, 'webhook']).enabled = v; })}
                  />
                </div>
                <FieldLabel label="Webhook secret ref">
                  <TextInput value={asString(webhook.secret)} onChange={(v) => updateDoc(onChange, doc, (n) => { ensureNestedObject(n, ['devops', 'products', productId, 'webhook']).secret = v || undefined; })} mono />
                </FieldLabel>
              </div>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <SubsectionTitle>Repos</SubsectionTitle>
                  <button
                    type="button"
                    onClick={() => updateDoc(onChange, doc, (n) => {
                      ensureArray(ensureNestedObject(n, ['devops', 'products', productId]), 'repos')
                        .push({ name: productId, repoUrl: '', branch: 'develop', username: 'maxiron', pat: '' });
                    })}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Plus className="w-3 h-3" /> Add repo
                  </button>
                </div>
                {repos.map((repo, rIdx) => (
                  <div key={`${productId}-repo-${rIdx}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-gray-100 dark:border-gray-800 pt-2">
                    <FieldLabel label="Name">
                      <TextInput value={asString(repo.name)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureNestedObject(n, ['devops', 'products', productId]), 'repos') as Record<string, unknown>[];
                        arr[rIdx] = { ...objectRecord(arr[rIdx]), name: v };
                      })} />
                    </FieldLabel>
                    <FieldLabel label="Branch">
                      <TextInput value={asString(repo.branch)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureNestedObject(n, ['devops', 'products', productId]), 'repos') as Record<string, unknown>[];
                        arr[rIdx] = { ...objectRecord(arr[rIdx]), branch: v };
                      })} />
                    </FieldLabel>
                    <FieldLabel label="Repo URL" className="md:col-span-2">
                      <TextInput value={asString(repo.repoUrl)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureNestedObject(n, ['devops', 'products', productId]), 'repos') as Record<string, unknown>[];
                        arr[rIdx] = { ...objectRecord(arr[rIdx]), repoUrl: v };
                      })} mono />
                    </FieldLabel>
                    <FieldLabel label="PAT ref">
                      <TextInput value={asString(repo.pat)} onChange={(v) => updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureNestedObject(n, ['devops', 'products', productId]), 'repos') as Record<string, unknown>[];
                        arr[rIdx] = { ...objectRecord(arr[rIdx]), pat: v };
                      })} mono />
                    </FieldLabel>
                    <div className="md:col-span-2 flex justify-end">
                      <RemoveButton label="Remove repo" onClick={() => updateDoc(onChange, doc, (n) => {
                        const p = ensureNestedObject(n, ['devops', 'products', productId]);
                        const arr = [...(ensureArray(p, 'repos') as unknown[])];
                        arr.splice(rIdx, 1);
                        p.repos = arr;
                      })} />
                    </div>
                  </div>
                ))}
              </div>
            </ItemCard>
          );
        })}
        <button
          type="button"
          onClick={() => updateDoc(onChange, doc, (n) => {
            const p = ensureNestedObject(n, ['devops', 'products']);
            const id = `product-${Object.keys(p).length + 1}`;
            p[id] = { workspaceDir: `../${id}`, repos: [{ name: id, repoUrl: '', branch: 'main' }] };
          })}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          <Plus className="h-3.5 w-3.5" />
          Add product
        </button>
      </div>

      {/* ── Lifecycle Defaults ─────────────────────────────────────────────── */}
      <SubsectionTitle>Lifecycle Defaults</SubsectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FieldLabel label="Manage base domain">
          <TextInput
            value={asString(lifecycleManageDefaults.baseDomain)}
            onChange={(v) => updateDoc(onChange, doc, (n) => {
              ensureNestedObject(n, ['devops', 'lifecycle', 'defaults', 'manage']).baseDomain = v || undefined;
            })}
          />
        </FieldLabel>
        <FieldLabel label="Default Hetzner type">
          <TextInput
            value={asString(objectRecord(lifecycleManageDefaults.hetzner).serverType)}
            onChange={(v) => updateDoc(onChange, doc, (n) => {
              ensureNestedObject(n, ['devops', 'lifecycle', 'defaults', 'manage', 'hetzner']).serverType = v || undefined;
            })}
            mono
          />
        </FieldLabel>
        <FieldLabel label="Default SSH user">
          <TextInput
            value={asString(objectRecord(lifecycleManageDefaults.ssh).user)}
            onChange={(v) => updateDoc(onChange, doc, (n) => {
              ensureNestedObject(n, ['devops', 'lifecycle', 'defaults', 'manage', 'ssh']).user = v || undefined;
            })}
            mono
          />
        </FieldLabel>
      </div>
    </SectionBox>
  );
}
